import { useEffect, useState } from "react";
import { CalendarDays, X } from "lucide-react";

const TIMES = [];
for (let h = 6; h <= 21; h += 1) ["00", "20", "40"].forEach((m) => TIMES.push(`${String(h).padStart(2, "0")}:${m}`));

const dayLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { weekday: "short", day: "numeric", month: "short" });

/** Flow 16: one day and one time (20-minute steps) for each session of each plan this week. */
const WeekPlannerCard = ({ api, studentId, mobileType = false, time, onDone }) => {
  const [week, setWeek] = useState(null);
  const [picks, setPicks] = useState({});
  const [error, setError] = useState("");
  const [closed, setClosed] = useState(false);
  const [editing, setEditing] = useState(null);
  const text = mobileType ? "text-[15px]" : "text-[13px]";
  const field = `rounded-xl border border-gray-200 bg-white px-2 py-1.5 ${text} outline-none focus:border-[#25D366]`;

  useEffect(() => {
    api.weekPlan(studentId).then(setWeek).catch((problem) => setError(problem.message || "Semaine indisponible."));
  }, [api, studentId]);

  const run = async (call) => {
    setError("");
    try {
      setWeek(await call());
    } catch (problem) {
      setError(problem.message || "Action impossible.");
    }
  };

  if (!week) return <div className="self-start rounded-2xl bg-white p-3 text-xs text-gray-500 shadow-sm" data-testid="week-plan-loading">{error || "Chargement…"}</div>;
  const left = week.plans.reduce((sum, plan) => sum + plan.toPlace, 0);
  const edit = !closed && (editing ?? left > 0);
  const lateToday = week.days[0] === new Date().toLocaleDateString("en-CA") && new Date().getHours() >= 16;
  const firstDay = (lateToday && week.days[1]) || week.days[0] || "";
  const pick = (planId) => picks[planId] || { date: firstDay, time: "16:00" };
  const setPick = (planId, patch) => setPicks((current) => ({ ...current, [planId]: { ...pick(planId), ...patch } }));

  return (
    <div className="w-[94%] sm:w-[88%] max-w-[23rem] self-start rounded-2xl rounded-tl-sm bg-white p-2.5 sm:p-3 shadow-sm" data-testid="week-plan-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E9FBF0] text-[#189346]"><CalendarDays className="h-4 w-4" /></span>
        <p className={`font-semibold text-[#122118] ${text}`}>Ma semaine</p>
        <span className="ml-auto rounded-full bg-[#F3FCF7] px-2 py-0.5 text-[11px] font-semibold text-[#189346]" data-testid="week-plan-left">
          {left ? `${left} à placer` : "Tout est placé"}
        </span>
      </div>
      {week.plans.map((plan) => (
        <div key={plan.id} className="mt-3 border-t border-gray-100 pt-2" data-testid={`week-plan-${plan.id}`}>
          <p className={`font-semibold text-[#122118] ${text}`}>{plan.title}</p>
          <p className="text-[11px] text-[#4a5f53]">{plan.doneThisWeek}/{plan.perWeek} faites cette semaine</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {week.sessions.filter((row) => row.planId === plan.id).map((row) => (
              <span key={row.id} className="inline-flex items-center gap-1 rounded-full bg-[#E9FBF0] px-2 py-1 text-[12px] text-[#0a2914]" data-testid="week-session-chip">
                {dayLabel(row.date)} · {row.time}{row.status === "done" ? " ✓" : ""}
                {row.status === "planned" && edit && (
                  <button type="button" aria-label="Retirer" onClick={() => run(() => api.removeSession(row.id))} data-testid="week-session-remove"><X className="h-3 w-3" /></button>
                )}
              </span>
            ))}
          </div>
          {plan.toPlace > 0 && edit && week.days.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <select className={field} value={pick(plan.id).date} onChange={(e) => setPick(plan.id, { date: e.target.value })} data-testid="week-pick-day">
                {week.days.map((day) => <option key={day} value={day}>{dayLabel(day)}</option>)}
              </select>
              <select className={field} value={pick(plan.id).time} onChange={(e) => setPick(plan.id, { time: e.target.value })} data-testid="week-pick-time">
                {TIMES.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
              <button type="button" onClick={() => run(() => api.addSession({ planId: plan.id, ...pick(plan.id) }))} className={`rounded-full bg-[#25D366] px-3 py-1.5 font-semibold text-[#0a2914] ${text}`} data-testid="week-add-session">
                Ajouter
              </button>
            </div>
          )}
        </div>
      ))}
      {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="week-plan-error">{error}</p>}
      {!closed && (
        <div className="mt-3 flex gap-2">
          {!edit && (
            <button type="button" onClick={() => setEditing(true)} className={`flex-1 rounded-full border border-gray-200 px-3 py-2 font-semibold text-[#2d5a3d] ${text}`} data-testid="week-plan-edit">
              Modifier
            </button>
          )}
          <button type="button" onClick={() => { setClosed(true); onDone?.(left === 0); }} className={`flex-[2] rounded-full bg-[#25D366] px-3 py-2 font-semibold text-[#0a2914] ${text}`} data-testid="week-plan-done">
            Confirmer mon horaire
          </button>
        </div>
      )}
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </div>
  );
};

export default WeekPlannerCard;
